/**
 * The agora registry: what an Agora is, how its address resolves to one, and how
 * the list of added agoras survives a reload.
 *
 * An Agora is public configuration only -- an address, a name, a theme, and the
 * issuer/project ids needed to sign in and reach its mail. Never a token:
 * those live in `sessionStorage`, namespaced per agora, in `auth.ts`. This
 * module's job ends at "which agora, reached how" -- signing in and reading
 * mail from it are somebody else's.
 */

import { getDomain } from 'tldts';

export type AgoraTheme = {
  accent: string;
  mark?: string;
};

export type Agora = {
  /** The canonical address, never read from the agora's own config -- an id
   *  the agora itself picked could collide with another agora's, and the address
   *  is the one thing that is already unique. See `canonicalAddress`. */
  id: string;
  address: string;
  name: string;
  theme: AgoraTheme;
  issuer: string;
  clientId: string;
  organisationId: string;
  mailSessionUrl: string;
  mailProjectId: string;
  messagingProjectId: string;
  /** The files project's audience (agora#353), requested as an extra sign-in
   *  scope the same way `mailProjectId`/`messagingProjectId` are. Optional,
   *  unlike those two: every agora already has mail and chat, but Files is
   *  off until an operator provisions its ZITADEL project -- an agora with
   *  no `filesProjectId` simply never asks for that audience, and
   *  `App.tsx` keeps Files off for it. */
  filesProjectId?: string;
  /** The mail server's authserv-id, its Stalwart `hostname` (agora#348).
   *  `@wtfalch/email` reads a message's `Authentication-Results` only from a
   *  header that names this server, so a COUNTER proposal's sender can be
   *  called verified. Optional: unset, no sender is ever verified and every
   *  proposal shows the unverified-sender warning. */
  mailAuthServId?: string;
  /** The one collection (`@wtfalch/files`' `createCollection`) every file
   *  this agora uploads goes into. Files has no route to list an
   *  organisation's collections and an ordinary member cannot create one
   *  (`files.collections:create` needs the admin role, not `files_member`),
   *  so this has to be provisioned once by an operator and published here --
   *  it cannot be discovered or created lazily client-side. Optional for the
   *  same reason `filesProjectId` is: unset until Files is provisioned for
   *  this company. Browsing and downloading files needs only
   *  `filesProjectId`; uploading also needs this. */
  filesCollectionId?: string;
  /** Whether this company has Chat (decided 2026-10-01: off in v1). Absent
   *  means off, and everything Chat draws, calls included, goes with it.
   *  Foundry flips it per org; `chatEnabled` is the one reader. */
  chat?: { enabled: boolean };
  /** Whether this company has calls in chat rooms. Absent means off.
   *  Calls ride on Chat, so they are drawn only when Chat is on too. */
  calls?: { enabled: boolean };
  /** Whether this company has working hours (`@wtfalch/hr`, served by
   *  `apps/hr` behind `/_hr`). Absent means off: the Settings row and the
   *  colleague's hours are not drawn. agora#258. */
  hours?: { enabled: boolean };
  /** Whether this company has Tasks (`@wtfalch/tasks`, behind `/_tasks`).
   *  Absent means off: the rail has no Tasks entry and `/tasks` says Tasks
   *  is not turned on. Foundry writes it once the company's tasks service
   *  is deployed. */
  tasks?: { enabled: boolean };
  /** Whether this company allows encrypted channels (agora#135). Absent
   *  means off: crypto is never started, and no lock, badge or Encryption
   *  dialog is drawn. Needs Chat on too. */
  encryptedChannels?: { enabled: boolean };
  /** Whether this company signs in on the agora's own address (`/sign-in/`,
   *  proxied to the company's login by Caddy's `SIGNIN_UPSTREAM`). Absent
   *  means off: sign-in goes to the issuer as before. Foundry writes it. */
  signInInApp?: boolean;
};

const HEX_COLOUR = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
// authForAgora (auth.ts) puts organisationId straight into an OAuth scope
// string (`urn:zitadel:iam:org:id:${organisationId}`), and @wtfalch/auth's
// browser client joins extraScopes on whitespace -- a well-known config
// could otherwise smuggle in extra scopes via a value like "x openid foo"
// (agora#431 review). ZITADEL's own org ids are this shape.
const ORG_ID = /^[0-9A-Za-z_-]+$/;

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

/**
 * The canonical host for an address somebody typed: `Mail.Example.com/x`
 * becomes `mail.example.com`, `localhost:47831` stays as it is. A scheme or
 * path pasted in by mistake is dropped. Throws when there is no host.
 *
 * This host is also the agora's id, and the id is the whole of what keeps one
 * agora's session store apart from another's (`storeForAgora`). So it must be
 * injective: two addresses share an id only when they name the same server.
 * An earlier id that folded punctuation to `-` gave `mail.acme.com` and
 * `mail-acme.com` the same store, and adding the second sent the first's
 * token to it.
 */
export function canonicalAddress(input: string): string {
  const trimmed = input.trim();
  const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  let host = '';
  try {
    host = new URL(withScheme).host;
    // A non-special scheme keeps an opaque host (`wtf://a%2Fb` gives
    // `a%2Fb`) that is no address at all: re-parse it the way it is used.
    new URL(`http://${host}`);
  } catch {
    host = '';
  }
  if (!host) throw new Error(`${input} is not an address.`);
  return host;
}

function isLocalAddress(address: string): boolean {
  return new URL(`http://${address}`).hostname === 'localhost';
}

/**
 * `oikos.host` is a real TLD's own registrable domain, not one the public
 * suffix list's private section lists the way it lists `github.io`, so
 * `getDomain` folds every tenant's address on it to the same `oikos.host`.
 * Both `isMailOnAddress` and `isIssuerOnAddress` match this apex by their
 * own stricter rule instead of the generic same-registrable-domain one.
 *
 * Reuses `getDomain` itself rather than a hand-written regex: an earlier
 * version matched a host one label below the apex (`/^([a-z0-9-]+\.)?oikos\.host$/`),
 * so a deeper one (`x.evil-agora.oikos.host`) fell through to the generic
 * rule. A later regex (`/(^|\.)oikos\.host$/`) fixed that but still missed a
 * trailing-dot host (`acme.oikos.host.`), which a browser and `new URL()`
 * both treat as identical to `acme.oikos.host`. `getDomain` already
 * normalises both, so asking it the same question the generic rule asks
 * -- "what is this host's registrable domain" -- answers both at once.
 */
function isSharedApexHost(host: string): boolean {
  return getDomain(host, { allowPrivateDomains: true }) === 'oikos.host';
}

/**
 * An Agora's mail server must sit on the same registrable domain as the agora's
 * address: `agora.example.com` may name `mail.example.com`, but not
 * `mail.other.com`. Without this, a hostile address could name a real issuer
 * and client -- so the sign-in is genuine -- and its own mail server, which
 * would then receive the mail token.
 *
 * The domain comes from the public suffix list, private section included, so
 * `a.co.uk` and `evil.co.uk` are two companies, and so are `a.github.io` and
 * `b.github.io`. A host with no registrable domain (an IP address) must match
 * exactly. A `localhost` address is exempt, for development. The port is
 * ignored: whoever controls a host controls its ports.
 *
 * On the shared `oikos.host` apex the mail host must equal the agora host
 * exactly, for every address on the apex, not only the `<slug>-agora`
 * shape: the JMAP session URL is always built from the agora's own origin
 * (`mailSessionUrl`), so a real config's mail host never differs from it,
 * and unlike the issuer there is no separate `-mail` slug to route to.
 */
function isMailOnAddress(address: string, mailSessionUrl: string): boolean {
  if (isLocalAddress(address)) return true;
  const agoraHost = new URL(`http://${address}`).hostname;
  const mailHost = new URL(mailSessionUrl).hostname;
  if (isSharedApexHost(agoraHost)) return mailHost === agoraHost;
  const agoraDomain = getDomain(agoraHost, { allowPrivateDomains: true });
  if (agoraDomain === null) return mailHost === agoraHost;
  return getDomain(mailHost, { allowPrivateDomains: true }) === agoraDomain;
}

/**
 * A tier-1 company's own name on the shared `oikos.host` apex -- the part
 * before `-agora`, lower-cased -- or `null` for anything else, an address
 * on a company's own custom domain included. `isIssuerOnAddress` matches
 * this apex by slug instead, and refuses outright rather than falling back
 * to the generic domain rule for anything else on it.
 */
function oikosAgoraSlug(agoraHost: string): string | null {
  const match = /^([a-z0-9](?:[a-z0-9-]*[a-z0-9])?)-agora\.oikos\.host$/i.exec(agoraHost);
  return match?.[1] ? match[1].toLowerCase() : null;
}

/**
 * An Agora's issuer must sit on the same registrable domain as the agora's
 * own address -- `agora.acme.com` may trust `auth.acme.com`, never
 * `auth.other.com` -- or, for a tier-1 company on the shared `oikos.host`
 * apex, the same `<slug>` under `-auth.oikos.host` rather than `-agora`
 * (`oikosAgoraSlug`'s own doc explains why that apex needs its own rule).
 * Without this, a discovered issuer and client id could name anyone, and
 * `auth.ts` would build a live OIDC client from them regardless of who
 * controls the address that named them (agora#349). A `localhost` address
 * is exempt, for development. Considered and rejected: a verified registry,
 * an explicit trust step, a signed descriptor -- see agora#349.
 */
function isIssuerOnAddress(address: string, issuer: string): boolean {
  if (isLocalAddress(address)) return true;
  const agoraHost = new URL(`http://${address}`).hostname;
  const issuerHost = new URL(issuer).hostname;
  const slug = oikosAgoraSlug(agoraHost);
  if (slug !== null) return issuerHost.toLowerCase() === `${slug}-auth.oikos.host`;
  if (isSharedApexHost(agoraHost)) return false;
  const agoraDomain = getDomain(agoraHost, { allowPrivateDomains: true });
  if (agoraDomain === null) return issuerHost === agoraHost;
  return getDomain(issuerHost, { allowPrivateDomains: true }) === agoraDomain;
}

/** Mail and Chat share the agora's exact origin, including its port. Do not
 * infer another host from the company's domain or use a stored legacy URL. */
export function agoraOrigin(agora: Pick<Agora, 'address'>): string {
  const address = canonicalAddress(agora.address);
  return `${isLocalAddress(address) ? 'http' : 'https'}://${address}`;
}

export function mailSessionUrl(agora: Pick<Agora, 'address'>): string {
  return `${agoraOrigin(agora)}/.well-known/jmap`;
}

export function chatExchangeUrl(agora: Pick<Agora, 'address'>): string {
  return `${agoraOrigin(agora)}/_chat/exchange`;
}

/** The origin's same-origin files proxy, `/_files`. A `createFilesClient`
 *  builds every request path (`/v1/...`) from this root itself, so this
 *  names only the proxy's mount point, not one of its routes. */
export function filesUrl(agora: Pick<Agora, 'address'>): string {
  return `${agoraOrigin(agora)}/_files`;
}

/** Where the browser reaches the calls service: `/_calls` on the agora's own
 *  origin, which Caddy strips and proxies to `AGORA_CALLS_UPSTREAM`. The SDK
 *  appends `/v1/...` and the Matrix gateway paths under it, so the gateway
 *  never reaches Agora's own `/_matrix` route to chat. */
export function callsUrl(agora: Pick<Agora, 'address'>): string {
  return `${agoraOrigin(agora)}/_calls`;
}

/** Where the browser reaches the working-hours service: `/_hr` on the agora's
 *  own origin, which Caddy strips and proxies to `AGORA_HR_UPSTREAM`. */
export function hoursUrl(agora: Pick<Agora, 'address'>): string {
  return `${agoraOrigin(agora)}/_hr`;
}

const CHAT_DEVICE_KEY = 'wtfalch-agora:chat-device:';

/**
 * The id this agora, on this device, uses for the chat exchange -- made once
 * and kept in `localStorage` so it survives a sign-out and a reload.
 * `createChatClient`'s `deviceId` needs this stable across sign-ins so MAS
 * keeps one Matrix device for the browser's whole life; a fresh id every
 * session would mean a fresh device every session instead. Every access is
 * wrapped, same as the rest of this module's storage -- a browser that
 * refuses it gets a device id that does not survive a reload, not a crash.
 */
export function chatDeviceId(agoraId: string): string {
  const key = `${CHAT_DEVICE_KEY}${agoraId}`;
  try {
    const existing = localStorage.getItem(key);
    if (existing) return existing;
    const fresh = crypto.randomUUID();
    localStorage.setItem(key, fresh);
    return fresh;
  } catch {
    return `${agoraId}-ephemeral`;
  }
}

/** https always, or http when the URL's own host is `localhost` -- an Agora's
 *  issuer and mail session both have to earn the same trust the agora's own
 *  address did to be reached at all. */
function isHttpsOrLocalUrl(value: unknown): value is string {
  if (!isNonEmptyString(value)) return false;
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return false;
  }
  if (url.protocol === 'https:') return true;
  return url.protocol === 'http:' && url.hostname === 'localhost';
}

type FeatureFlag = { enabled: boolean };

/** Absent is fine (the feature is off); present must be `{ enabled: boolean }`. */
function isFeatureFlag(value: unknown): value is FeatureFlag | undefined {
  if (value === undefined) return true;
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as Record<string, unknown>).enabled === 'boolean'
  );
}

/** Where the browser reaches the tasks service: `/_tasks` on the agora's own
 *  origin, which Caddy strips and proxies to `AGORA_TASKS_UPSTREAM`. */
export function tasksUrl(agora: Pick<Agora, 'address'>): string {
  return `${agoraOrigin(agora)}/_tasks`;
}

/** The one reader of whether this company has Tasks. Absent is off. */
export function tasksEnabled(agora: Pick<Agora, 'tasks'>): boolean {
  return agora.tasks?.enabled === true;
}

/** The one reader of whether this company has calls. Absent config is off. */
export function callsEnabled(agora: Pick<Agora, 'calls'>): boolean {
  return agora.calls?.enabled === true;
}

/** The one reader of whether this company has working hours. Absent is off. */
export function hoursEnabled(agora: Pick<Agora, 'hours'>): boolean {
  return agora.hours?.enabled === true;
}

const CALLS_DEVICE_KEY = 'wtfalch-agora:calls-device:';

/**
 * The stable per-browser id sent as `x-calls-device`. The SDK takes 8 to 128
 * characters of `A-Za-z0-9_-`, so a UUID fits and a host name does not --
 * the fallback for a browser that refuses `localStorage` is a fresh UUID that
 * lasts only for this page, never one built from the agora's id.
 */
export function callsDeviceId(agoraId: string): string {
  const key = `${CALLS_DEVICE_KEY}${agoraId}`;
  try {
    const existing = localStorage.getItem(key);
    if (existing && /^[A-Za-z0-9_-]{8,128}$/.test(existing)) return existing;
    const fresh = crypto.randomUUID();
    localStorage.setItem(key, fresh);
    return fresh;
  } catch {
    return crypto.randomUUID();
  }
}

/** The one reader of whether this company has Chat. Absent config is off. */
export function chatEnabled(agora: Pick<Agora, 'chat'>): boolean {
  return agora.chat?.enabled === true;
}

/** The one reader of whether this company allows encrypted channels. Absent is off. */
export function encryptedChannelsEnabled(
  agora: Pick<Agora, 'chat' | 'encryptedChannels'>,
): boolean {
  return chatEnabled(agora) && agora.encryptedChannels?.enabled === true;
}

export type AgoraConfig = Omit<Agora, 'id' | 'address' | 'mailSessionUrl'> & {
  /** Accepted for migration from old configs; clients derive the agora URL. */
  mailSessionUrl?: string;
};

/**
 * Every field is required except `theme.mark`. An Agora's config is somebody
 * else's server, so a wrong or missing field is rejected rather than
 * defaulted -- a guessed issuer or a guessed colour is worse than an Agora that
 * refuses to add.
 */
export function parseAgoraConfig(
  raw: unknown,
): { ok: true; value: AgoraConfig } | { ok: false; error: string } {
  if (typeof raw !== 'object' || raw === null) {
    return { ok: false, error: 'not a JSON object' };
  }
  const r = raw as Record<string, unknown>;

  if (!isNonEmptyString(r.name)) return { ok: false, error: 'missing name' };

  if (typeof r.theme !== 'object' || r.theme === null) {
    return { ok: false, error: 'missing theme' };
  }
  const theme = r.theme as Record<string, unknown>;
  if (!isNonEmptyString(theme.accent) || !HEX_COLOUR.test(theme.accent)) {
    return { ok: false, error: 'bad theme.accent' };
  }
  if (theme.mark !== undefined && !isNonEmptyString(theme.mark)) {
    return { ok: false, error: 'bad theme.mark' };
  }

  if (!isHttpsOrLocalUrl(r.issuer)) return { ok: false, error: 'bad issuer' };
  if (!isNonEmptyString(r.clientId)) return { ok: false, error: 'missing clientId' };
  if (!isNonEmptyString(r.organisationId) || !ORG_ID.test(r.organisationId))
    return { ok: false, error: 'bad organisationId' };
  if (r.mailSessionUrl !== undefined && !isHttpsOrLocalUrl(r.mailSessionUrl))
    return { ok: false, error: 'bad mailSessionUrl' };
  // The project ids enter scope strings too (`authForAgora`), so they get
  // the org id's shape check: a space would smuggle in another scope.
  if (!isNonEmptyString(r.mailProjectId)) return { ok: false, error: 'missing mailProjectId' };
  if (!ORG_ID.test(r.mailProjectId)) return { ok: false, error: 'bad mailProjectId' };
  if (!isNonEmptyString(r.messagingProjectId))
    return { ok: false, error: 'missing messagingProjectId' };
  if (!ORG_ID.test(r.messagingProjectId)) return { ok: false, error: 'bad messagingProjectId' };
  if (r.mailAuthServId !== undefined && !isNonEmptyString(r.mailAuthServId))
    return { ok: false, error: 'bad mailAuthServId' };
  if (
    r.filesProjectId !== undefined &&
    (!isNonEmptyString(r.filesProjectId) || !ORG_ID.test(r.filesProjectId))
  )
    return { ok: false, error: 'bad filesProjectId' };
  if (r.filesCollectionId !== undefined && !isNonEmptyString(r.filesCollectionId))
    return { ok: false, error: 'bad filesCollectionId' };
  if (!isFeatureFlag(r.chat)) return { ok: false, error: 'bad chat' };
  if (!isFeatureFlag(r.calls)) return { ok: false, error: 'bad calls' };
  if (!isFeatureFlag(r.hours)) return { ok: false, error: 'bad hours' };
  if (!isFeatureFlag(r.tasks)) return { ok: false, error: 'bad tasks' };
  if (!isFeatureFlag(r.encryptedChannels)) return { ok: false, error: 'bad encryptedChannels' };
  if (r.signInInApp !== undefined && typeof r.signInInApp !== 'boolean')
    return { ok: false, error: 'bad signInInApp' };

  return {
    ok: true,
    value: {
      name: r.name,
      theme:
        theme.mark !== undefined
          ? { accent: theme.accent, mark: theme.mark }
          : { accent: theme.accent },
      issuer: r.issuer,
      clientId: r.clientId,
      organisationId: r.organisationId,
      ...(typeof r.mailSessionUrl === 'string' ? { mailSessionUrl: r.mailSessionUrl } : {}),
      mailProjectId: r.mailProjectId,
      messagingProjectId: r.messagingProjectId,
      ...(typeof r.mailAuthServId === 'string' ? { mailAuthServId: r.mailAuthServId } : {}),
      ...(typeof r.filesProjectId === 'string' ? { filesProjectId: r.filesProjectId } : {}),
      ...(typeof r.filesCollectionId === 'string'
        ? { filesCollectionId: r.filesCollectionId }
        : {}),
      ...(r.chat !== undefined ? { chat: { enabled: r.chat.enabled } } : {}),
      ...(r.calls !== undefined ? { calls: { enabled: r.calls.enabled } } : {}),
      ...(r.hours !== undefined ? { hours: { enabled: r.hours.enabled } } : {}),
      ...(r.tasks !== undefined ? { tasks: { enabled: r.tasks.enabled } } : {}),
      ...(r.encryptedChannels !== undefined
        ? { encryptedChannels: { enabled: r.encryptedChannels.enabled } }
        : {}),
      ...(typeof r.signInInApp === 'boolean' ? { signInInApp: r.signInInApp } : {}),
    },
  };
}

function wellKnownUrl(address: string): string {
  const scheme = isLocalAddress(address) ? 'http' : 'https';
  return `${scheme}://${address}/.well-known/wtfalch-agora.json`;
}

/** Fetch and validate an Agora's public config. Throws with a message fit to
 *  show the person who typed the address -- there is no other caller. */
export async function resolveAgora(input: string): Promise<Agora> {
  const address = canonicalAddress(input);
  const url = wellKnownUrl(address);
  let response: Response;
  try {
    response = await fetch(url);
  } catch {
    throw new Error(`Could not reach ${address}.`);
  }
  if (!response.ok) {
    throw new Error(`${address} answered ${response.status}.`);
  }
  let raw: unknown;
  try {
    raw = await response.json();
  } catch {
    throw new Error(`${address} did not answer with JSON.`);
  }
  const parsed = parseAgoraConfig(raw);
  if (!parsed.ok) {
    throw new Error(`${address}'s agora config is invalid: ${parsed.error}.`);
  }
  if (parsed.value.mailSessionUrl && !isMailOnAddress(address, parsed.value.mailSessionUrl)) {
    throw new Error(`${address}'s mail server is not on ${address}.`);
  }
  if (!isIssuerOnAddress(address, parsed.value.issuer)) {
    throw new Error(`${address}'s issuer is not trusted for ${address}.`);
  }
  return { id: address, address, ...parsed.value, mailSessionUrl: mailSessionUrl({ address }) };
}

const AGORAS_KEY = 'wtfalch-agora:agoras';
const LAST_AGORA_KEY = 'wtfalch-agora:last-agora';

/** The added agoras, in the order they were added. Never a token: this is the
 *  same shape `resolveAgora` returns. Every access is wrapped -- a browser
 *  that refuses storage (private mode, a full quota) loses the list on
 *  reload, not the app. */
export function loadAgoras(): Agora[] {
  try {
    const raw = localStorage.getItem(AGORAS_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.flatMap(storedAgora) : [];
  } catch {
    return [];
  }
}

/**
 * A saved agora gets the same checks `resolveAgora` gave it, every load. A list
 * saved before a rule existed -- or edited by hand -- must not hand a token to
 * a mail server the rule now refuses. An agora that fails is dropped; adding it
 * again re-fetches its config and says why it is refused.
 */
function storedAgora(value: unknown): Agora[] {
  if (typeof value !== 'object' || value === null) return [];
  const { id, address } = value as Record<string, unknown>;
  if (typeof address !== 'string' || id !== address) return [];
  try {
    if (canonicalAddress(address) !== address) return [];
  } catch {
    return [];
  }
  const parsed = parseAgoraConfig(value);
  if (!parsed.ok) return [];
  if (parsed.value.mailSessionUrl && !isMailOnAddress(address, parsed.value.mailSessionUrl))
    return [];
  if (!isIssuerOnAddress(address, parsed.value.issuer)) return [];
  return [{ id: address, address, ...parsed.value, mailSessionUrl: mailSessionUrl({ address }) }];
}

export function saveAgoras(agoras: readonly Agora[]): void {
  try {
    localStorage.setItem(AGORAS_KEY, JSON.stringify(agoras));
  } catch {
    // Best effort: the agora still works for this tab.
  }
}

/** Add an Agora, replacing any existing entry with the same id -- re-adding an
 *  already-known address refreshes its config rather than duplicating it. */
export function addAgora(agoras: readonly Agora[], agora: Agora): Agora[] {
  return [...agoras.filter((existing) => existing.id !== agora.id), agora];
}

/** Whether a freshly fetched config asks for different sign-in scopes than
 *  the saved one. A token issued under the old values is refused by the
 *  service whose project changed, so the saved session has to go. */
export function signInChanged(saved: Agora, fresh: Agora): boolean {
  return (
    saved.issuer !== fresh.issuer ||
    saved.clientId !== fresh.clientId ||
    saved.organisationId !== fresh.organisationId ||
    saved.mailProjectId !== fresh.mailProjectId ||
    saved.messagingProjectId !== fresh.messagingProjectId ||
    saved.filesProjectId !== fresh.filesProjectId
  );
}

export function getLastAgoraId(): string | null {
  try {
    return localStorage.getItem(LAST_AGORA_KEY);
  } catch {
    return null;
  }
}

export function setLastAgoraId(id: string): void {
  try {
    localStorage.setItem(LAST_AGORA_KEY, id);
  } catch {
    // Best effort.
  }
}
